import React, { useEffect, useState, useContext } from 'react';
import { Grid, FormLabel, FormHelperText, CircularProgress, Typography, FormControlLabel, Checkbox } from '@material-ui/core';
import { SecondaryButton, TextBox, CustomColorPicker } from '../../../../Core/FormInput';
import AppContext from '../../../../App/AppContext';
import { getWorkshopShiftsByID, postWorkshopShiftsAddUpdate } from '../../../../Core/Service/WorkshopShifts-service';

const DAYS = [
    { key: 'mondayActive', label: 'Mon', full: 'Monday' },
    { key: 'tuesdayActive', label: 'Tue', full: 'Tuesday' },
    { key: 'wednesdayActive', label: 'Wed', full: 'Wednesday' },
    { key: 'thursdayActive', label: 'Thu', full: 'Thursday' },
    { key: 'fridayActive', label: 'Fri', full: 'Friday' },
    { key: 'saturdayActive', label: 'Sat', full: 'Saturday' },
    { key: 'sundayActive', label: 'Sun', full: 'Sunday' }
];

// Normalize any truthy/falsy value from the API into a real boolean
const toBool = (val, fallback = true) => {
    if (val === undefined || val === null) return fallback;
    if (typeof val === 'boolean') return val;
    if (val === 1 || val === '1' || val === 'Yes' || val === 'yes' || val === 'true') return true;
    if (val === 0 || val === '0' || val === 'No' || val === 'no' || val === 'false') return false;
    return fallback;
};

// Build form state from any shift data object (list row or API response)
const buildStateFromShift = (d) => ({
    shiftName: d.shiftName || d.name || '',
    startTime: d.startTime ? d.startTime.substring(0, 5) : '08:00',
    endTime: d.endTime ? d.endTime.substring(0, 5) : '17:00',
    backgroundColour: d.backgroundColour || d.bgColour || '#EBF5FB',
    textColour: d.textColour || d.txtColour || '#1B4F72',
    displaySequence: d.displaySequence ?? 1,
    isActive: toBool(d.isActive, true),
    mondayActive: toBool(d.mondayActive, false),
    tuesdayActive: toBool(d.tuesdayActive, false),
    wednesdayActive: toBool(d.wednesdayActive, false),
    thursdayActive: toBool(d.thursdayActive, false),
    fridayActive: toBool(d.fridayActive, false),
    saturdayActive: toBool(d.saturdayActive, false),
    sundayActive: toBool(d.sundayActive, false),
    errors: {}
});

const BLANK_STATE = {
    shiftName: '',
    startTime: '08:00',
    endTime: '17:00',
    backgroundColour: '#EBF5FB',
    textColour: '#1B4F72',
    displaySequence: 1,
    isActive: true,
    errors: {},
    mondayActive: true,
    tuesdayActive: true,
    wednesdayActive: true,
    thursdayActive: true,
    fridayActive: true,
    saturdayActive: false,
    sundayActive: false
};

const AddUpdateWorkshopShifts = (props) => {
    const { shiftID, onClose, rowData } = props;
    const { showToast } = useContext(AppContext);

    const [loading, setLoading] = useState(false);
    // Use rowData from the grid as immediate initial state (contains all day fields)
    // so form shows correct values before the API refresh completes
    const [state, setState] = useState(() => (rowData ? buildStateFromShift(rowData) : { ...BLANK_STATE }));

    useEffect(() => {
        const fetchShift = async () => {
            if (shiftID) {
                setLoading(true);
                try {
                    const res = await getWorkshopShiftsByID(shiftID);
                    if (res?.success) {
                        // Support: res.data.table[0], res.data.list[0], res.data direct object
                        const raw = res.data ?? res;
                        const shiftData = Array.isArray(raw?.table)
                            ? raw.table[0]
                            : Array.isArray(raw?.list)
                            ? raw.list[0]
                            : (raw?.shiftID || raw?.shiftName) ? raw : null;

                        if (shiftData) {
                            setState((prev) => ({ ...prev, ...buildStateFromShift(shiftData), errors: {} }));
                        }
                    }
                } catch (error) {
                    console.error('Error loading shift:', error);
                    showToast('Failed to load shift details', 'error');
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchShift();
    }, [shiftID]);

    const handleInput = (e) => {
        const { name, value } = e.target;
        setState((prev) => ({
            ...prev,
            [name]: name === 'displaySequence' ? (value === '' ? '' : parseInt(value, 10)) : value,
            errors: { ...prev.errors, [name]: '' }
        }));
    };

    const handleCheckbox = (e) => {
        const { name, checked } = e.target;
        setState((prev) => ({ ...prev, [name]: checked }));
    };

    const toggleDay = (key) => {
        setState((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const validate = () => {
        const tempErrors = {};
        if (!state.shiftName.trim()) tempErrors.shiftName = 'Shift Name is required';
        if (!state.startTime) tempErrors.startTime = 'Start Time is required';
        if (!state.endTime) tempErrors.endTime = 'End Time is required';
        if (state.displaySequence === '' || state.displaySequence < 0) {
            tempErrors.displaySequence = 'Display Sequence is required';
        }
        setState((prev) => ({ ...prev, errors: tempErrors }));
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            const payload = {
                ShiftName: state.shiftName,
                StartTime: state.startTime + ':00',
                EndTime: state.endTime + ':00',
                BackgroundColour: state.backgroundColour,
                TextColour: state.textColour,
                DisplaySequence: state.displaySequence,
                IsActive: state.isActive,
                ShiftID: shiftID || null,
                MondayActive: state.mondayActive,
                TuesdayActive: state.tuesdayActive,
                WednesdayActive: state.wednesdayActive,
                ThursdayActive: state.thursdayActive,
                FridayActive: state.fridayActive,
                SaturdayActive: state.saturdayActive,
                SundayActive: state.sundayActive
            };

            const res = await postWorkshopShiftsAddUpdate(payload);
            if (res.success || res.data?.success) {
                showToast(res.data?.message || res.message || `Shift ${shiftID ? 'updated' : 'created'} successfully`, 'success');
                onClose(true);
            } else {
                showToast(res.data?.message || res.message || 'Failed to save shift details', 'error');
            }
        } catch (error) {
            console.error('Error saving shift:', error);
            showToast('An error occurred while saving the shift', 'error');
        } finally {
            setLoading(false);
        }
    };

    const activeDaysCount = DAYS.filter((d) => state[d.key]).length;

    return (
        <div>
            {loading ? (
                <Grid container justify="center" alignItems="center" style={{ minHeight: '200px', flexDirection: 'column' }}>
                    <CircularProgress />
                    <Typography style={{ fontWeight: 'bold', marginTop: '12px' }}>Loading...</Typography>
                </Grid>
            ) : (
                <Grid container spacing={2}>

                    {/* ── Shift Name ── */}
                    <Grid item xs={12}>
                        <FormLabel required className="mandatoryfields">Shift Name</FormLabel>
                        <TextBox name="shiftName" value={state.shiftName} onChange={handleInput} fullWidth />
                        {state.errors.shiftName && <FormHelperText error>{state.errors.shiftName}</FormHelperText>}
                    </Grid>

                    {/* ── Shift Active Days ── */}
                    <Grid item xs={12}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                            <FormLabel style={{ margin: 0 }}>Shift Active Days</FormLabel>
                            <span style={{
                                fontSize: 10,
                                fontWeight: 700,
                                backgroundColor: activeDaysCount > 0 ? '#DBEAFE' : '#F3F4F6',
                                color: activeDaysCount > 0 ? '#1D4ED8' : '#9CA3AF',
                                padding: '2px 9px',
                                borderRadius: 20
                            }}>
                                {activeDaysCount}/{DAYS.length} active
                            </span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
                            {DAYS.map((day) => {
                                const isActive = !!state[day.key];
                                return (
                                    <div
                                        key={day.key}
                                        onClick={() => toggleDay(day.key)}
                                        title={day.full}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 2,
                                            padding: '8px 4px',
                                            borderRadius: 8,
                                            border: `1.5px solid ${isActive ? '#2563EB' : '#E5E7EB'}`,
                                            backgroundColor: isActive ? '#EFF6FF' : '#F9FAFB',
                                            cursor: 'pointer',
                                            transition: 'all 0.15s',
                                            userSelect: 'none'
                                        }}
                                    >
                                        <Checkbox
                                            checked={isActive}
                                            size="small"
                                            name={day.key}
                                            onChange={handleCheckbox}
                                            onClick={(e) => e.stopPropagation()}
                                            style={{ padding: 0, color: isActive ? '#2563EB' : '#CBD5E1' }}
                                        />
                                        <span style={{
                                            fontSize: 11,
                                            fontWeight: 700,
                                            color: isActive ? '#1D4ED8' : '#9CA3AF',
                                            lineHeight: 1
                                        }}>{day.label}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </Grid>

                    {/* ── Start & End Time ── */}
                    <Grid item xs={12} sm={6}>
                        <FormLabel required className="mandatoryfields">Start Time</FormLabel>
                        <TextBox type="time" name="startTime" value={state.startTime} onChange={handleInput} InputLabelProps={{ shrink: true }} fullWidth />
                        {state.errors.startTime && <FormHelperText error>{state.errors.startTime}</FormHelperText>}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormLabel required className="mandatoryfields">End Time</FormLabel>
                        <TextBox type="time" name="endTime" value={state.endTime} onChange={handleInput} InputLabelProps={{ shrink: true }} fullWidth />
                        {state.errors.endTime && <FormHelperText error>{state.errors.endTime}</FormHelperText>}
                    </Grid>

                    {/* ── Background Colour ── */}
                    <Grid item xs={12} sm={6} container direction="column" spacing={1}>
                        <Grid item>
                            <FormLabel>Background Colour</FormLabel>
                        </Grid>
                        <Grid item>
                            <CustomColorPicker
                                color={state.backgroundColour}
                                onChange={(color) => setState((prev) => ({ ...prev, backgroundColour: color.hex }))}
                                width="100%"
                            />
                        </Grid>
                    </Grid>

                    {/* ── Text Colour ── */}
                    <Grid item xs={12} sm={6} container direction="column" spacing={1}>
                        <Grid item>
                            <FormLabel>Text Colour</FormLabel>
                        </Grid>
                        <Grid item>
                            <CustomColorPicker
                                color={state.textColour}
                                onChange={(color) => setState((prev) => ({ ...prev, textColour: color.hex }))}
                                width="100%"
                            />
                        </Grid>
                    </Grid>

                    {/* ── Display Sequence ── */}
                    <Grid item xs={12} sm={6}>
                        <FormLabel required className="mandatoryfields">Display Sequence</FormLabel>
                        <TextBox type="number" name="displaySequence" value={state.displaySequence} onChange={handleInput} inputProps={{ min: 1 }} fullWidth />
                        {state.errors.displaySequence && <FormHelperText error>{state.errors.displaySequence}</FormHelperText>}
                    </Grid>

                    {/* ── Is Active ── */}
                    <Grid item xs={12} sm={6} style={{ display: 'flex', alignItems: 'center', paddingTop: '24px' }}>
                        <FormControlLabel
                            control={<Checkbox name="isActive" checked={state.isActive} onChange={handleCheckbox} color="primary" />}
                            label="Is Active?"
                        />
                    </Grid>

                    {/* ── Submit ── */}
                    <Grid item xs={12} style={{ marginTop: '24px' }}>
                        <SecondaryButton fullWidth onClick={handleSubmit}>
                            Submit
                        </SecondaryButton>
                    </Grid>

                </Grid>
            )}
        </div>
    );
};

export default AddUpdateWorkshopShifts;
